import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { Form, Row, Col, Button, Card } from 'react-bootstrap';
import './Stock.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCar, 
    faCalendarAlt, 
    faPalette, 
    faTachometerAlt, 
    faCog, 
    faMoneyBillWave,
    faSort,
    faFilter,
    faSearch,
    faTimesCircle,
    faGasPump,
    faArrowLeft,
    faMapMarkerAlt,
    faShareAlt,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { API_ENDPOINT, BASE_URL } from '../../config/config';

const Stock = () => {
    const [cars, setCars] = useState([]);
    const [filteredCars, setFilteredCars] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [filters, setFilters] = useState({
        priceRange: { min: '', max: '' },
        make: '',
        model: '',
        year: '',
        transmission: '',
        color: '',
        mileageRange: { min: '', max: '' }
    });
    const [sortBy, setSortBy] = useState('newest');
    const [showFilters, setShowFilters] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const handleShare = (car) => {
        const shareUrl = `${window.location.origin}/car/${car._id}`;
        const shareTitle = `${car.year} ${car.make} ${car.model} | About Cars`;
        const shareText = `Check out this ${car.year} ${car.make} ${car.model} for PKR ${car.price?.toLocaleString()} on About Cars!`;

        if (navigator.share) {
            navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(() => {});
        } else {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopiedId(car._id);
                setTimeout(() => setCopiedId(null), 2500);
            });
        }
    };
    const [availableMakes, setAvailableMakes] = useState([]);
    const [availableModels, setAvailableModels] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableColors, setAvailableColors] = useState([]);

    // Add capitalizeFirst function
    const capitalizeFirst = (text) => {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    };

    const getCars = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINT}/stock/cars`);
            if (response.data && response.data.success) {
                const activeCars = response.data.data.filter(car => car.status === 'active');
                setCars(activeCars);
                setFilteredCars(activeCars);
                
                // Extract unique values for filters
                const makes = [...new Set(activeCars.map(car => car.make))];
                const models = [...new Set(activeCars.map(car => car.model))];
                const years = [...new Set(activeCars.map(car => car.year))];
                const colors = [...new Set(activeCars.map(car => car.color))];
                
                setAvailableMakes(makes);
                setAvailableModels(models);
                setAvailableYears(years);
                setAvailableColors(colors);
            }
        } catch (error) {
            console.error('Error fetching car data:', error);
            setError(error.message || 'Error fetching car data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCars();
    }, []);

    // Apply filters
    const applyFilters = () => {
        let filtered = [...cars];

        if (filters.priceRange.min) {
            filtered = filtered.filter(car => car.price >= Number(filters.priceRange.min));
        }
        if (filters.priceRange.max) {
            filtered = filtered.filter(car => car.price <= Number(filters.priceRange.max));
        }
        if (filters.make) {
            filtered = filtered.filter(car => car.make.toLowerCase() === filters.make.toLowerCase());
        }
        if (filters.model) {
            filtered = filtered.filter(car => car.model.toLowerCase() === filters.model.toLowerCase());
        }
        if (filters.year) {
            filtered = filtered.filter(car => car.year === filters.year);
        }
        if (filters.transmission) {
            filtered = filtered.filter(car => car.transmission.toLowerCase() === filters.transmission.toLowerCase());
        }
        if (filters.color) {
            filtered = filtered.filter(car => car.color.toLowerCase() === filters.color.toLowerCase());
        }
        if (filters.mileageRange.min) {
            filtered = filtered.filter(car => car.mileage >= Number(filters.mileageRange.min));
        }
        if (filters.mileageRange.max) {
            filtered = filtered.filter(car => car.mileage <= Number(filters.mileageRange.max));
        }

        // Apply sorting
        switch (sortBy) {
            case 'priceAsc':
                filtered.sort((a, b) => a.price - b.price);
                break;
            case 'priceDesc':
                filtered.sort((a, b) => b.price - a.price);
                break;
            case 'newest':
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'mileageAsc':
                filtered.sort((a, b) => a.mileage - b.mileage);
                break;
            default:
                break;
        }

        setFilteredCars(filtered);
    };

    useEffect(() => {
        applyFilters();
    }, [filters, sortBy]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const resetFilters = () => {
        setFilters({
            priceRange: { min: '', max: '' },
            make: '',
            model: '',
            year: '',
            transmission: '',
            color: '',
            mileageRange: { min: '', max: '' }
        });
        setSortBy('newest');
    };

    return (
        <div className="stock-page">
            <div className="page-header">
                <div className="container-fluid px-3">
                    <div className="back-section mb-2">
                        <Link to="/" className="back-link">
                            <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
                        </Link>
                    </div>
                    <h2 className="text-center mb-0">
                        <FontAwesomeIcon icon={faCar} className="me-2" />
                        Vehicles in Stock
                    </h2>
                </div>
            </div>
            <div className="container-fluid px-3">
                <div className="mb-3">
                    <Button
                        variant={showFilters ? 'danger' : 'outline-danger'}
                        onClick={() => setShowFilters(prev => !prev)}
                        className="filter-toggle-btn"
                        style={{ borderRadius: '25px', padding: '8px 22px', fontWeight: 600, letterSpacing: '0.3px' }}
                    >
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </div>

                {showFilters && (
                <Card className="mb-3 filter-card" style={{ animation: 'fadeSlideDown 0.25s ease' }}>
                    <Card.Body className="p-3">
                        <div className="filter-header">
                            <Button variant="outline-danger" onClick={resetFilters} className="reset-btn">
                                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                Reset Filters
                            </Button>
                        </div>
                        <div className="filter-sections">
                            <div className="filter-section">
                                <Row className="g-2">
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faCar} className="me-2" />
                                                Make
                                            </Form.Label>
                                            <Form.Select 
                                                value={filters.make}
                                                onChange={(e) => handleFilterChange('make', e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="">All Makes</option>
                                                {availableMakes.map(make => (
                                                    <option key={make} value={make}>{capitalizeFirst(make)}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faCar} className="me-2" />
                                                Model
                                            </Form.Label>
                                            <Form.Select
                                                value={filters.model}
                                                onChange={(e) => handleFilterChange('model', e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="">All Models</option>
                                                {availableModels.map(model => (
                                                    <option key={model} value={model}>{capitalizeFirst(model)}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                                Year
                                            </Form.Label>
                                            <Form.Select
                                                value={filters.year}
                                                onChange={(e) => handleFilterChange('year', e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="">All Years</option>
                                                {availableYears.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faPalette} className="me-2" />
                                                Color
                                            </Form.Label>
                                            <Form.Select
                                                value={filters.color}
                                                onChange={(e) => handleFilterChange('color', e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="">All Colors</option>
                                                {availableColors.map(color => (
                                                    <option key={color} value={color}>{capitalizeFirst(color)}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="filter-section">
                                <h4 className="section-title">Price & Features</h4>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                                                Price Range (PKR)
                                            </Form.Label>
                                            <div className="range-inputs">
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Min"
                                                    value={filters.priceRange.min}
                                                    onChange={(e) => handleFilterChange('priceRange', { ...filters.priceRange, min: e.target.value })}
                                                    className="custom-input"
                                                />
                                                <span className="range-separator">to</span>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Max"
                                                    value={filters.priceRange.max}
                                                    onChange={(e) => handleFilterChange('priceRange', { ...filters.priceRange, max: e.target.value })}
                                                    className="custom-input"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faTachometerAlt} className="me-2" />
                                                Mileage Range (km)
                                            </Form.Label>
                                            <div className="range-inputs">
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Min"
                                                    value={filters.mileageRange.min}
                                                    onChange={(e) => handleFilterChange('mileageRange', { ...filters.mileageRange, min: e.target.value })}
                                                    className="custom-input"
                                                />
                                                <span className="range-separator">to</span>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Max"
                                                    value={filters.mileageRange.max}
                                                    onChange={(e) => handleFilterChange('mileageRange', { ...filters.mileageRange, max: e.target.value })}
                                                    className="custom-input"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                                Transmission
                                            </Form.Label>
                                            <Form.Select
                                                value={filters.transmission}
                                                onChange={(e) => handleFilterChange('transmission', e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="">All Types</option>
                                                <option value="Automatic">Automatic</option>
                                                <option value="Manual">Manual</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="filter-group">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faSort} className="me-2" />
                                                Sort Results
                                            </Form.Label>
                                            <Form.Select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                className="custom-select"
                                            >
                                                <option value="newest">Newest First</option>
                                                <option value="priceAsc">Price: Low to High</option>
                                                <option value="priceDesc">Price: High to Low</option>
                                                <option value="mileageAsc">Mileage: Low to High</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                )}

                <div className="row g-3">
                    {loading ? (
                        <div className="col-12 text-center p-5">
                            <Spinner animation="border" style={{ color: '#ed1c24' }} />
                        </div>
                    ) : error ? (
                        <div className="col-12">
                            <div className="alert alert-danger">{error}</div>
                        </div>
                    ) : filteredCars.length === 0 ? (
                        <div className="col-12 text-center">
                            <div className="no-results p-5">
                                <FontAwesomeIcon icon={faSearch} className="mb-3" style={{ fontSize: '3rem', color: '#ed1c24' }} />
                                <p className="h4 text-muted">No cars available matching your criteria.</p>
                            </div>
                        </div>
                    ) : (
                        filteredCars.map((car) => (
                            <div className="col-md-4" key={car._id}>
                                <div className="car-card">
                                    <div className="car-image">
                                        <img 
                                            src={car.images && car.images.length > 0 
                                                ? `${BASE_URL}${car.images[0]}`
                                                : '/placeholder-car.jpg'} 
                                            alt={`${car.make} ${car.model}`}
                                            className="car-main-image"
                                        />
                                    </div>
                                    <div className="car-content">
                                        <h3 className="car-title">
                                            <FontAwesomeIcon icon={faCar} className="car-icon" />
                                            {car.year} {car.make} {car.model}
                                        </h3>
                                        <div className="car-specs">
                                            <div className="spec-item">
                                                <FontAwesomeIcon icon={faPalette} style={{ color: '#ed1c24' }} />
                                                <span>Color: {capitalizeFirst(car.color)}</span>
                                            </div>
                                            <div className="spec-item">
                                                <FontAwesomeIcon icon={faTachometerAlt} style={{ color: '#ed1c24' }} />
                                                <span>Mileage: {car.mileage ? car.mileage.toLocaleString() : '0'} km</span>
                                            </div>
                                            <div className="spec-item">
                                                <FontAwesomeIcon icon={faCog} style={{ color: '#ed1c24' }} />
                                                <span>Transmission: {capitalizeFirst(car.transmission)}</span>
                                            </div>
                                            <div className="spec-item">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#ed1c24' }} />
                                                <span>Reg City: {capitalizeFirst(car.registration_city)}</span>
                                            </div>
                                        </div>
                                        <div className="car-price">
                                            <span className="price-amount">PKR {car.price ? car.price.toLocaleString() : '0'}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                                            <Link to={`/car/${car._id}`} className="view-details-btn" style={{ flex: 1 }}>
                                                View Details
                                            </Link>
                                            <button
                                                onClick={() => handleShare(car)}
                                                title="Share this car"
                                                className="share-car-btn"
                                            >
                                                <FontAwesomeIcon icon={copiedId === car._id ? faCheck : faShareAlt} />
                                                {copiedId === car._id ? 'Copied!' : 'Share'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

        </div>
    );
};

export default Stock;
